import {
  collectWorldTriangles,
  type WorldTrianglesSource,
} from '@viewer/lib/worldTriangles'
import { now, packBroadRequest } from './broadPhase'
import { createLocalClashEngine } from './clashEngine'
import {
  normalizeClashConfig,
  type ClashConfig,
  type ClashElementInput,
  type ClashEngine,
  type ClashProgress,
  type ClashResult,
  type ClashRun,
  type ClashStats,
  type NarrowElement,
} from './clashTypes'

// ---------------------------------------------------------------------------
// Orquestrador: modelos carregados no viewer -> lista de conflitos.
//
//   recolha de caixas -> broad-phase -> lotes de pares -> narrow-phase
//
// DECISOES QUE VALE A PENA CONHECER
//
// Origem local. Toda a narrow-phase corre com posicoes RELATIVAS a um
// ponto dentro do modelo (a mediana dos centros das caixas). Num modelo
// georreferenciado as coordenadas andam na casa dos 1,3 milhoes de
// metros e um Float32 la tem um passo de ~0,125 m: sem a origem, duas
// paredes encostadas apareceriam a atravessar-se e o resultado seria
// lixo. E o mesmo remedio que as Vistas 2D levaram (beta.69).
//
// Caixas corrompidas. O `getBoxes` do Fragments devolve, numa minoria
// de elementos, caixas em 1e12 (documentado em views2d/modelBox.ts).
// Uma caixa dessas nao pode entrar na grelha: encheria a tabela ou
// obrigaria a celulas do tamanho de um pais. Filtram-se pela mediana
// dos centros e contam-se em `stats.skippedBoxes`.
//
// Memoria. A geometria NUNCA e carregada toda de uma vez. Os pares sao
// agrupados em lotes com um numero limitado de elementos distintos; por
// lote pede-se a geometria desses elementos, corre-se a narrow-phase e
// larga-se tudo (com o worker, os buffers sao transferidos e a thread
// principal fica sem eles de imediato). Um elemento que apareca em
// varios lotes e recolhido varias vezes: e o preco de nao segurar o
// modelo inteiro em RAM.
//
// Determinismo. Elementos pela ordem em que os modelos os dao, pares
// ordenados por indice na broad-phase, resultados reordenados no fim
// pela mesma chave. Mesma entrada, mesma saida.
// ---------------------------------------------------------------------------

interface BoxLike {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
}

export interface ClashModelSource extends WorldTrianglesSource {
  modelId?: string
  /** Caixas de MUNDO por elemento. Sem isto o modelo e ignorado: a
   *  broad-phase nao tem como trabalhar. */
  getBoxes?: (ids: number[]) => Promise<BoxLike[]>
  getItemsOfCategories?: (
    categories: RegExp[],
  ) => Promise<Record<string, number[]>>
}

export interface RunClashOptions {
  config?: Partial<ClashConfig> | null
  /** Motor a usar. Por omissao corre na propria thread; a UI deve
   *  passar `createWorkerClashEngine()`. */
  engine?: ClashEngine
  onProgress?: (p: ClashProgress) => void
  signal?: { aborted: boolean }
  /** Restringe a analise a estes ids, por modelId. */
  ids?: Record<string, number[]>
  /** Categoria de um elemento, quando o chamador ja a tem em cache. */
  categoryOf?: (modelId: string, localId: number) => string | null | undefined
  /** Elementos distintos por lote da narrow-phase. */
  batchElements?: number
}

/** Elementos distintos por lote. 192 elementos de geometria media sao
 *  poucos MB e chegam para amortizar o custo por mensagem. */
const DEFAULT_BATCH_ELEMENTS = 192
/** Extensao acima da qual uma caixa e dada como corrompida, em metros. */
const MAX_BOX_EXTENT_M = 1e5
/** Distancia maxima do centro de uma caixa a mediana dos centros. */
const MAX_BOX_OFFSET_M = 1e6

function median(values: number[]): number {
  if (values.length === 0) return 0
  const s = values.slice().sort((a, b) => a - b)
  const mid = s.length >> 1
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

function usableBox(b: BoxLike | null | undefined): boolean {
  if (!b || !b.min || !b.max) return false
  const v = [b.min.x, b.min.y, b.min.z, b.max.x, b.max.y, b.max.z]
  if (!v.every((n) => Number.isFinite(n))) return false
  return (
    b.max.x - b.min.x <= MAX_BOX_EXTENT_M &&
    b.max.y - b.min.y <= MAX_BOX_EXTENT_M &&
    b.max.z - b.min.z <= MAX_BOX_EXTENT_M
  )
}

/** Mapa localId -> categoria IFC de um modelo. O Fragments so da o mapa
 *  ao contrario, por isso inverte-se (mesmo padrao do export KMZ e das
 *  Vistas 2D). Falha em silencio: sem categorias os filtros por
 *  categoria simplesmente nao apanham nada. */
async function categoryMapOf(
  model: ClashModelSource,
): Promise<Map<number, string>> {
  const map = new Map<number, string>()
  if (!model.getItemsOfCategories) return map
  try {
    const byCat = await model.getItemsOfCategories([/.*/])
    for (const [cat, ids] of Object.entries(byCat)) {
      if (!ids) continue
      for (const id of ids) map.set(id, cat)
    }
  } catch {
    // Sem categorias.
  }
  return map
}

interface CollectedElements {
  elements: ClashElementInput[]
  /** Indice do modelo de cada elemento, para ir buscar a geometria. */
  modelOf: number[]
  origin: [number, number, number]
  skippedBoxes: number
}

async function collectElements(
  models: readonly ClashModelSource[],
  opts: RunClashOptions,
  report: (done: number, total: number) => void,
): Promise<CollectedElements> {
  const raw: {
    modelIndex: number
    modelId: string
    localId: number
    category: string | null
    box: BoxLike
  }[] = []
  let skippedBoxes = 0

  for (let m = 0; m < models.length; m++) {
    const model = models[m]
    const modelId = model.modelId ?? `model-${m}`
    if (!model.getBoxes) continue
    let ids: number[]
    try {
      ids = opts.ids?.[modelId] ?? (await model.getItemsIdsWithGeometry())
    } catch {
      ids = []
    }
    if (!ids || ids.length === 0) continue
    let boxes: BoxLike[]
    try {
      boxes = await model.getBoxes(ids)
    } catch {
      boxes = []
    }
    const cats = opts.categoryOf ? null : await categoryMapOf(model)
    for (let i = 0; i < ids.length; i++) {
      const box = boxes[i]
      if (!usableBox(box)) {
        skippedBoxes++
        continue
      }
      const category = opts.categoryOf
        ? (opts.categoryOf(modelId, ids[i]) ?? null)
        : (cats?.get(ids[i]) ?? null)
      raw.push({ modelIndex: m, modelId, localId: ids[i], category, box })
    }
    report(m + 1, models.length)
  }

  // Origem = mediana dos centros. Robusta a caixas parvas que tenham
  // escapado ao filtro anterior.
  const cx = median(raw.map((r) => (r.box.min.x + r.box.max.x) / 2))
  const cy = median(raw.map((r) => (r.box.min.y + r.box.max.y) / 2))
  const cz = median(raw.map((r) => (r.box.min.z + r.box.max.z) / 2))
  const origin: [number, number, number] = [cx, cy, cz]

  const elements: ClashElementInput[] = []
  const modelOf: number[] = []
  for (const r of raw) {
    const ex = (r.box.min.x + r.box.max.x) / 2 - cx
    const ey = (r.box.min.y + r.box.max.y) / 2 - cy
    const ez = (r.box.min.z + r.box.max.z) / 2 - cz
    if (
      Math.abs(ex) > MAX_BOX_OFFSET_M ||
      Math.abs(ey) > MAX_BOX_OFFSET_M ||
      Math.abs(ez) > MAX_BOX_OFFSET_M
    ) {
      skippedBoxes++
      continue
    }
    elements.push({
      modelId: r.modelId,
      localId: r.localId,
      category: r.category,
      box: {
        min: [r.box.min.x, r.box.min.y, r.box.min.z],
        max: [r.box.max.x, r.box.max.y, r.box.max.z],
      },
    })
    modelOf.push(r.modelIndex)
  }
  return { elements, modelOf, origin, skippedBoxes }
}

/** Agrupa os pares em lotes com no maximo `maxElements` elementos
 *  distintos. Os pares vem ordenados por indice, por isso elementos
 *  vizinhos caem no mesmo lote e a geometria e reaproveitada. */
export function batchPairs(
  pairs: Uint32Array,
  maxElements: number,
): Uint32Array[] {
  const batches: Uint32Array[] = []
  const current: number[] = []
  let members = new Set<number>()
  const flush = (): void => {
    if (current.length === 0) return
    batches.push(Uint32Array.from(current))
    current.length = 0
    members = new Set<number>()
  }
  for (let i = 0; i < pairs.length; i += 2) {
    const a = pairs[i]
    const b = pairs[i + 1]
    const grows = (members.has(a) ? 0 : 1) + (members.has(b) ? 0 : 1)
    if (members.size + grows > maxElements && current.length > 0) flush()
    members.add(a)
    members.add(b)
    current.push(a, b)
  }
  flush()
  return batches
}

/** Corre a deteccao de conflitos. Nunca lanca por causa dos dados do
 *  modelo: APIs em falta ou geometria ilegivel dao menos resultados,
 *  com as contagens em `stats`. */
export async function runClash(
  models: readonly ClashModelSource[],
  opts: RunClashOptions = {},
): Promise<ClashRun> {
  const t0 = now()
  const config = normalizeClashConfig(opts.config)
  const engine = opts.engine ?? createLocalClashEngine()
  const ownsEngine = !opts.engine
  const batchSize = Math.max(2, opts.batchElements ?? DEFAULT_BATCH_ELEMENTS)
  const results: ClashResult[] = []
  const stats: ClashStats = {
    elements: 0,
    skippedBoxes: 0,
    skippedHeavy: 0,
    candidatePairs: 0,
    testedPairs: 0,
    hard: 0,
    clearance: 0,
    truncatedPairs: false,
    ms: { total: 0, collect: 0, broad: 0, narrow: 0 },
    cellSize: 0,
    cancelled: false,
  }
  const emit = (
    phase: ClashProgress['phase'],
    done: number,
    total: number,
  ): void => {
    opts.onProgress?.({ phase, done, total, results: results.length })
  }
  const aborted = (): boolean => opts.signal?.aborted === true

  try {
    // --- 1. Elementos e caixas ---------------------------------------
    const tCollect = now()
    emit('collect', 0, models.length)
    const collected = await collectElements(models, opts, (d, t) =>
      emit('collect', d, t),
    )
    stats.ms.collect = now() - tCollect
    stats.elements = collected.elements.length
    stats.skippedBoxes = collected.skippedBoxes
    if (collected.elements.length < 2 || aborted()) {
      stats.cancelled = aborted()
      stats.ms.total = now() - t0
      emit('done', 0, 0)
      return { results, stats, config }
    }

    // --- 2. Broad-phase ----------------------------------------------
    const tBroad = now()
    emit('broad', 0, collected.elements.length)
    const broadReq = packBroadRequest(collected.elements, config)
    const broad = await engine.broadPhase(broadReq)
    stats.ms.broad = now() - tBroad
    stats.cellSize = broad.cellSize
    stats.truncatedPairs = broad.truncated
    stats.candidatePairs = Math.floor(broad.pairs.length / 2)
    emit('broad', collected.elements.length, collected.elements.length)
    if (stats.candidatePairs === 0 || aborted()) {
      stats.cancelled = aborted()
      stats.ms.total = now() - t0
      emit('done', 0, 0)
      return { results, stats, config }
    }

    // --- 3. Narrow-phase por lotes -----------------------------------
    const tNarrow = now()
    const batches = batchPairs(broad.pairs, batchSize)
    const heavy = new Set<number>()
    let donePairs = 0
    emit('narrow', 0, stats.candidatePairs)

    for (const batch of batches) {
      if (aborted()) {
        stats.cancelled = true
        break
      }
      // Pares ainda validos (sem elementos ja dados como pesados).
      const usable: number[] = []
      for (let i = 0; i < batch.length; i += 2) {
        if (heavy.has(batch[i]) || heavy.has(batch[i + 1])) continue
        usable.push(batch[i], batch[i + 1])
      }
      donePairs += batch.length / 2
      if (usable.length === 0) {
        emit('narrow', donePairs, stats.candidatePairs)
        continue
      }

      // Geometria dos elementos deste lote, por modelo.
      const needed = new Set<number>(usable)
      const byModel = new Map<number, number[]>()
      for (const idx of needed) {
        const m = collected.modelOf[idx]
        const list = byModel.get(m)
        if (list) list.push(idx)
        else byModel.set(m, [idx])
      }
      const elements: NarrowElement[] = []
      for (const [m, idxs] of byModel) {
        const model = models[m]
        const localIds = idxs.map((i) => collected.elements[i].localId)
        const byLocalId = new Map<number, number>()
        idxs.forEach((i, k) => byLocalId.set(localIds[k], i))
        let items
        try {
          items = await collectWorldTriangles(model, {
            ids: localIds,
            origin: collected.origin,
            maxTriangles: config.maxTrianglesPerElement * localIds.length,
          })
        } catch {
          continue
        }
        const got = new Set<number>()
        for (const item of items.items) {
          const index = byLocalId.get(item.localId)
          if (index === undefined) continue
          if (item.indices.length / 3 > config.maxTrianglesPerElement) {
            heavy.add(index)
            stats.skippedHeavy++
            continue
          }
          got.add(index)
          elements.push({
            index,
            positions: item.positions,
            indices: item.indices,
          })
        }
        // Elementos que nao vieram: geometria ilegivel ou acima do
        // tecto de triangulos (a recolha corta antes de devolver o
        // item). Marcam-se para nao voltarem a ser pedidos lote apos
        // lote.
        for (const index of idxs) {
          if (got.has(index) || heavy.has(index)) continue
          heavy.add(index)
          stats.skippedHeavy++
        }
      }

      const present = new Set(elements.map((e) => e.index))
      const pairs: number[] = []
      for (let i = 0; i < usable.length; i += 2) {
        if (!present.has(usable[i]) || !present.has(usable[i + 1])) continue
        pairs.push(usable[i], usable[i + 1])
      }
      if (pairs.length === 0) {
        emit('narrow', donePairs, stats.candidatePairs)
        continue
      }

      const res = await engine.narrowPhase({
        origin: collected.origin,
        config,
        pairs: Uint32Array.from(pairs),
        elements,
      })
      stats.testedPairs += res.testedPairs
      for (const r of res.results) {
        const ea = collected.elements[r.a]
        const eb = collected.elements[r.b]
        if (!ea || !eb) continue
        const [ox, oy, oz] = collected.origin
        results.push({
          pair: {
            modelA: ea.modelId,
            localIdA: ea.localId,
            modelB: eb.modelId,
            localIdB: eb.localId,
          },
          kind: r.kind,
          categoryA: ea.category ?? null,
          categoryB: eb.category ?? null,
          depth: r.depth,
          distance: r.distance,
          point: [r.point[0] + ox, r.point[1] + oy, r.point[2] + oz],
          bbox: {
            min: [r.bbox.min[0] + ox, r.bbox.min[1] + oy, r.bbox.min[2] + oz],
            max: [r.bbox.max[0] + ox, r.bbox.max[1] + oy, r.bbox.max[2] + oz],
          },
          triangles: r.triangles,
          evidence: r.evidence,
          truncated: r.truncated,
        })
        if (r.kind === 'hard') stats.hard++
        else stats.clearance++
      }
      emit('narrow', donePairs, stats.candidatePairs)
    }
    stats.ms.narrow = now() - tNarrow
  } finally {
    if (ownsEngine) engine.dispose()
  }

  // Ordem estavel, independente da ordem de chegada dos lotes.
  results.sort(comparePairs)
  stats.ms.total = now() - t0
  emit('done', stats.candidatePairs, stats.candidatePairs)
  return { results, stats, config }
}

function comparePairs(a: ClashResult, b: ClashResult): number {
  const byModelA = a.pair.modelA.localeCompare(b.pair.modelA)
  if (byModelA !== 0) return byModelA
  if (a.pair.localIdA !== b.pair.localIdA) return a.pair.localIdA - b.pair.localIdA
  const byModelB = a.pair.modelB.localeCompare(b.pair.modelB)
  if (byModelB !== 0) return byModelB
  return a.pair.localIdB - b.pair.localIdB
}

/** Ordena por gravidade: hard primeiro (mais profundo em cima), depois
 *  folgas (mais apertada em cima). Estavel: pares iguais mantem a ordem
 *  deterministica de `runClash`. Aqui para a UI nao ter de inventar
 *  criterios. */
export function sortBySeverity(results: readonly ClashResult[]): ClashResult[] {
  return results.slice().sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'hard' ? -1 : 1
    if (a.kind === 'hard') return (b.depth ?? 0) - (a.depth ?? 0)
    return (a.distance ?? 0) - (b.distance ?? 0)
  })
}

/** Chave estavel de um conflito. Serve para a UI guardar estados
 *  (aceite / resolvido) que sobrevivem a nova corrida.
 *
 *  CANONICA: os dois elementos vao por ordem fixa (`modelo:id` menor
 *  primeiro), seja qual for a ordem em que o motor devolveu o par. A
 *  ordem do motor depende da ordem dos modelos (que lado e A, que
 *  ficheiro abriu primeiro), e com ela trocada a triagem gravada deixava
 *  de casar: pareceres "ausentes" e conflitos outra vez "novos". */
export function clashKey(r: ClashResult): string {
  return canonicalPairKey(
    `${r.pair.modelA}:${r.pair.localIdA}`,
    `${r.pair.modelB}:${r.pair.localIdB}`,
    r.kind,
  )
}

function canonicalPairKey(x: string, y: string, kind: string): string {
  return x <= y ? `${x}|${y}|${kind}` : `${y}|${x}|${kind}`
}

/** Converte uma chave gravada (incluindo as antigas, com o par pela
 *  ordem do motor) para a forma canonica. Uma chave que nao se perceba
 *  devolve-se tal e qual. */
export function canonicalClashKey(key: string): string {
  const parts = key.split('|')
  if (parts.length !== 3) return key
  const [x, y, kind] = parts
  if (!x.includes(':') || !y.includes(':') || !kind) return key
  return canonicalPairKey(x, y, kind)
}
